import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { api, formatMXN, formatApiError } from "../lib/api";
import { usePlanActual } from "../lib/planes";
import { useAuth } from "../contexts/AuthContext";
import { COPY_WIZARD as W, PASOS } from "../lib/wizard";
import { formatoDinero } from "../lib/money";
import { TEXTOS, texto, camposAnticipoServicio } from "../lib/anticipos";
import { avisoAgenda } from "../lib/doctores";
import PageHeader from "../components/PageHeader";
import Modal from "../components/Modal";
import DoctorAvailabilityEditor from "../components/DoctorAvailabilityEditor";
import AusenciasDoctor from "../components/AusenciasDoctor";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Stethoscope, PlusCircle, X, Sparkles } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4"];

// CL.1 (DT.267): los topes ya NO se escriben aquí. Llegan en `limites` de
// `GET /doctors?con_limites=1`, calculados por la MISMA función del backend que los aplica
// (`_limites_profesionales`). El contador de la cabecera sólo se pinta cuando el backend da
// topes por separado (demo y planes legacy); para los planes del catálogo, que limitan la SUMA
// de profesionales, o si el tope no se pudo leer, no se pinta nada: nunca un número inventado.
// El mensaje de error real sigue saliendo del backend; el botón "Nuevo" no se bloquea aquí.

// Agrupa filas de doctor_availability (el horario real, S3.1/S3.2) en un texto legible.
// Los días con exactamente las mismas franjas se agrupan juntos, ej.:
// "Lun, Mar, Mié, Jue, Vie · 10:00-14:00 y 16:00-19:00"
function formatAvailability(rows) {
  if (!rows || rows.length === 0) return "Sin horario configurado";
  const byDay = {};
  rows.forEach((r) => {
    const franja = `${r.start_time}-${r.end_time}`;
    (byDay[r.day_of_week] = byDay[r.day_of_week] || []).push(franja);
  });
  const groups = {};
  Object.keys(byDay).forEach((day) => {
    const franjas = byDay[day].slice().sort();
    const sig = franjas.join("|");
    (groups[sig] = groups[sig] || { days: [], franjas }).days.push(Number(day));
  });
  return Object.values(groups)
    .map((g) => `${g.days.sort((a, b) => a - b).map((d) => DAYS[d]).join(", ")} · ${g.franjas.join(" y ")}`)
    .join("; ");
}

function limitLabel(n) {
  return n === -1 ? "sin límite" : String(n);
}

const emptyForm = {
  name: "",
  specialty: "",
  color: "#2563EB",
  is_doctor: true,
  role_label: "",
};

function AvisoAgenda({ doctor, sinSofi, testid }) {
  const aviso = avisoAgenda(doctor, sinSofi);
  return aviso ? <p role="status" data-testid={testid} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{aviso}</p> : null;
}

const Doctors = forwardRef(function Doctors({ wizardPaso, incompletos, onSaved, onContinue, onExit }, ref) {
  const [doctors, setDoctors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [loadError, setLoadError] = useState(false);
  // TEN.4: en un plan sin Sofi no hay asistente al que hacer visible un servicio.
  const { sinSofi } = usePlanActual();
  // Corrección 1: escribir el anticipo de un servicio exige `configuracion`
  // (capacidades.MATRIZ: POST/PUT /api/doctor-services) y la demo no escribe (DT.71).
  const { user, puede } = useAuth() || {};
  const puedeEditarAnticipo = !user?.is_demo_session && !!puede?.("configuracion");

  const [limites, setLimites] = useState(null);
  const topesSeparados = limites?.modo === "separado";
  const doctorLimit = limites?.medicos;
  const providerLimit = limites?.prestadores;
  const doctorCount = doctors.filter((d) => d.is_doctor !== false).length;
  const providerCount = doctors.filter((d) => d.is_doctor === false).length;

  // Services state
  const [existingServices, setExistingServices] = useState([]); // fetched from DB (editing mode)
  const [newServices, setNewServices] = useState([]);            // pending to POST
  const [deletedIds, setDeletedIds] = useState([]);              // ids to soft-delete
  const svcNameRef = useRef('');
  const svcDurationRef = useRef(30);
  const svcPriceRef = useRef('');
  const svcRequiresApprovalRef = useRef(null);
  const svcBookableRef = useRef(null);
  // CL.4 A2 -- anticipo opcional por servicio. El monto se guarda como CADENA ("500.00").
  const [nuevoRequiereAnticipo, setNuevoRequiereAnticipo] = useState(false);
  const [nuevoAnticipoMonto, setNuevoAnticipoMonto] = useState("");

  // Horario (doctor_availability, S5b) -- arreglo plano de {day_of_week, start_time, end_time}
  const [availability, setAvailability] = useState([]);

  useImperativeHandle(ref, () => ({ guardar: () => open ? save(newServices) : Promise.resolve(true) }));

  const load = async () => {
    const { data } = await api.get("/doctors", { params: { con_limites: 1 } });
    setDoctors(data.items);
    setLimites(data.limites ?? null);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setLoadError(false);
    setEditing(null);
    setForm(emptyForm);
    setExistingServices([]);
    setNewServices([]);
    setDeletedIds([]);
    setAvailability([]);
    setOpen(true);
  };

  const openEdit = async (doc) => {
    setLoadError(false);
    setEditing(doc);
    setForm({
      name: doc.name,
      specialty: doc.specialty || "",
      color: doc.color || "#2563EB",
      is_doctor: doc.is_doctor !== false,
      role_label: doc.role_label || "",
    });
    setNewServices([]);
    setDeletedIds([]);
    try {
      if (!wizardPaso || wizardPaso === "servicios") {
        const { data } = await api.get(`/doctor-services/${doc.id}`);
        setExistingServices(data.items);
      }
    } catch {
      setExistingServices([]);
      if (wizardPaso) setLoadError(true);
    }
    try {
      if (!wizardPaso || wizardPaso === "horarios") {
        const { data } = await api.get(`/doctors/${doc.id}/availability`);
        setAvailability((data.items || []).map(({ day_of_week, start_time, end_time }) => ({ day_of_week, start_time, end_time })));
      }
    } catch {
      setAvailability([]);
      if (wizardPaso) setLoadError(true);
    }
    setOpen(true);
  };

  const handleAddService = () => {
    const name = (svcNameRef.current?.value || '').trim();
    const price = parseFloat(svcPriceRef.current?.value || '');
    const duration = parseInt(svcDurationRef.current?.value || '30', 10) || 30;
    const requires_approval = !!svcRequiresApprovalRef.current?.checked;
    const bookable_by_assistant = svcBookableRef.current ? !!svcBookableRef.current.checked : true;
    if (!name) { toast.error("El nombre del servicio es obligatorio"); return; }
    if (!svcPriceRef.current?.value || isNaN(price)) { toast.error("El precio es obligatorio (número válido)"); return; }
    const ant = camposAnticipoServicio(nuevoRequiereAnticipo, nuevoAnticipoMonto);
    if (ant.error) { toast.error(TEXTOS[ant.error]); return; }
    const newSvc = { name, duration_minutes: duration, price, requires_approval, bookable_by_assistant, ...ant.campos, _key: Date.now() };
    setNewServices((prev) => [...prev, newSvc]);
    toast.success(`Servicio "${name}" agregado`);
    if (svcNameRef.current) svcNameRef.current.value = '';
    if (svcPriceRef.current) svcPriceRef.current.value = '';
    if (svcDurationRef.current) svcDurationRef.current.value = '30';
    if (svcRequiresApprovalRef.current) svcRequiresApprovalRef.current.checked = false;
    if (svcBookableRef.current) svcBookableRef.current.checked = true;
    setNuevoRequiereAnticipo(false);
    setNuevoAnticipoMonto("");
  };

  // CL.4 A2 -- guarda el anticipo de un servicio YA existente (PUT inmediato, como las
  // banderas de arriba). Errores `{detail, code}` del backend por `formatApiError`.
  const guardarAnticipoExistente = async (svc, requiere, monto) => {
    const ant = camposAnticipoServicio(requiere, monto);
    if (ant.error) { toast.error(TEXTOS[ant.error]); return false; }
    try {
      const { data } = await api.put(`/doctor-services/${editing.id}/${svc.id}`, {
        name: svc.name,
        duration_minutes: svc.duration_minutes,
        price: svc.price,
        requires_approval: svc.requires_approval,
        bookable_by_assistant: svc.bookable_by_assistant,
        ...ant.campos,
      });
      setExistingServices((prev) => prev.map((s) => (s.id === svc.id
        ? { ...s, requiere_anticipo: data?.requiere_anticipo ?? ant.campos.requiere_anticipo,
            anticipo_sugerido: data?.anticipo_sugerido ?? s.anticipo_sugerido }
        : s)));
      toast.success(TEXTOS.servicio_guardado_ok);
      return true;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar el servicio");
      return false;
    }
  };

  const removeNewService = (key) => {
    setNewServices((prev) => prev.filter((s) => s._key !== key));
  };

  const removeExistingService = (id) => {
    setExistingServices((prev) => prev.filter((s) => s.id !== id));
    setDeletedIds((prev) => [...prev, id]);
  };

  // Cambia requires_approval/bookable_by_assistant de un servicio YA guardado -- PUT inmediato,
  // no espera al "Guardar" general (evita borrar+recrear el servicio, lo que le cambiaría el id
  // y rompería cualquier cita que ya lo referencie).
  const toggleExistingServiceFlag = async (svc, field) => {
    const updated = { ...svc, [field]: !svc[field] };
    try {
      await api.put(`/doctor-services/${editing.id}/${svc.id}`, {
        name: updated.name,
        duration_minutes: updated.duration_minutes,
        price: updated.price,
        requires_approval: updated.requires_approval,
        bookable_by_assistant: updated.bookable_by_assistant,
      });
      setExistingServices((prev) => prev.map((s) => (s.id === svc.id ? updated : s)));
    } catch {
      toast.error("No se pudo actualizar el servicio");
    }
  };

  const save = async (currentNewServices = []) => {
    if (saving || loadError) return false;
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return false; }
    if (!form.is_doctor && !form.role_label.trim()) { toast.error("Indica el rol (ej. Masajista, Enfermera)"); return false; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        specialty: form.specialty,
        color: form.color,
        is_doctor: form.is_doctor,
        role_label: form.is_doctor ? null : form.role_label.trim(),
      };
      let doc;

      if (editing) {
        doc = editing;
        if (!wizardPaso || wizardPaso === "medicos") {
          const { data } = await api.patch(`/doctors/${editing.id}`, payload);
          doc = data;
        }
        for (const sid of deletedIds) {
          await api.delete(`/doctor-services/${editing.id}/${sid}`);
          setDeletedIds(prev => prev.filter(id => id !== sid));
        }
      } else {
        const { data } = await api.post("/doctors", payload);
        doc = data;
        setEditing(doc);
      }

      if (!doc?.id) {
        toast.error("Error interno: no se pudo obtener el ID del doctor");
        return false;
      } else {
        // POST new services
        for (const svc of currentNewServices) {
          try {
            await api.post(`/doctor-services/${doc.id}`, {
              name: svc.name,
              duration_minutes: Number(svc.duration_minutes) || 30,
              price: Number(svc.price),
              requires_approval: !!svc.requires_approval,
              bookable_by_assistant: svc.bookable_by_assistant !== false,
              requiere_anticipo: !!svc.requiere_anticipo,
              anticipo_sugerido: svc.requiere_anticipo ? svc.anticipo_sugerido : null,
            });
            setNewServices(prev => prev.filter(item => item._key !== svc._key));
          } catch (e) {
            toast.error(`No se pudo guardar "${svc.name}": ${e?.response?.data?.detail ? formatApiError(e.response.data.detail) : e.message}`);
            return false;
          }
        }
        // Reemplaza el set completo de horario (doctor_availability) -- no es merge parcial.
        try {
          if (!wizardPaso || wizardPaso === "horarios") await api.put(`/doctors/${doc.id}/availability`, { slots: availability });
        } catch (e) {
          toast.error(`No se pudo guardar el horario: ${e?.response?.data?.detail || e.message}`);
          return false;
        }
      }

      toast.success(editing ? "Guardado" : (form.is_doctor ? "Médico creado" : "Prestador creado"));
      await load();
      setOpen(false);
      await onSaved?.();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteDoctor = async (doc) => {
    if (!window.confirm(`¿Eliminar a ${doc.name}?`)) return;
    setDeleting(doc.id);
    try {
      await api.delete(`/doctors/${doc.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      // Se ramifica por `code` TOP-LEVEL, nunca por el texto del mensaje (DT.60): el texto es
      // para la persona y puede reescribirse sin avisar. Aqui el 409 no es un fallo del sistema
      // sino una regla de negocio -- el historico de dinero no se borra (DT.238) -- asi que se
      // muestra el motivo completo del backend y se deja en pantalla el tiempo suficiente para
      // leerlo, en vez del "No se pudo eliminar" generico, que no dice ni por que ni que hacer.
      if (e?.response?.status === 409 && e?.response?.data?.code === "DOCTOR_CON_MOVIMIENTOS") {
        toast.error(formatApiError(e.response.data.detail), { duration: 9000 });
      } else {
        toast.error("No se pudo eliminar");
      }
    } finally {
      setDeleting(null);
    }
  };

  const allDisplayedServices = [
    ...existingServices,
    ...newServices.map((s) => ({ ...s, id: null, _pending: true })),
  ];

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-5xl mx-auto w-full">
      <PageHeader
        title={wizardPaso ? PASOS.find(p => p.id === wizardPaso)?.label : "Doctores"}
        subtitle="Administra el equipo médico, prestadores, horarios y sus servicios propios."
        actions={
          <div className="flex items-center gap-3">
            {topesSeparados && (
              <span className="text-xs text-slate-500 hidden sm:inline">
                {doctorCount} de {limitLabel(doctorLimit)} médico{doctorLimit !== 1 ? "s" : ""} · {providerCount} de {limitLabel(providerLimit)} prestador{providerLimit !== 1 ? "es" : ""}
              </span>
            )}
            {(!wizardPaso || wizardPaso === "medicos") && <button onClick={openCreate} className="inline-flex items-center gap-2 bg-brand-electric text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm">
              <Plus className="w-4 h-4" /> Nuevo
            </button>}
          </div>
        }
      />

      {doctors.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-12 text-center text-slate-500">
          <Stethoscope className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="font-medium">Sin doctores registrados</div>
          <button onClick={openCreate} className="mt-4 text-sm text-brand-electric hover:underline">Agregar el primero</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {doctors.map((doc) => {
            const isProvider = doc.is_doctor === false;
            return (
              <div key={doc.id} className="bg-white border border-slate-200 rounded-2xl shadow-card p-5 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full grid place-items-center text-white text-lg font-bold shrink-0" style={{ background: doc.color || "#2563EB" }}>
                    {doc.name[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                      {doc.name}
                      {isProvider && (
                        <span title="Prestador no médico" className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-600 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full shrink-0">
                          <Sparkles className="w-2.5 h-2.5" /> Prestador
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{isProvider ? (doc.role_label || "Prestador") : doc.specialty}</div>
                  </div>
                </div>
                <div className="text-xs text-slate-500">{formatAvailability(doc.availability)}</div>
                <AvisoAgenda doctor={doc} sinSofi={sinSofi} testid={`aviso-agenda-${doc.id}`} />
                {!isProvider && Array.isArray(incompletos) && <p className="text-sm" data-testid={`doctor-paso-${doc.id}`}>
                  {incompletos.includes(doc.id) ? W.pendiente : W.hecho}
                </p>}
                <div className="flex gap-2 mt-auto">
                  <button onClick={() => openEdit(doc)} className="flex-1 text-xs text-slate-600 border border-slate-200 rounded-lg py-1.5 hover:bg-slate-50 flex items-center justify-center gap-1">
                    <Pencil className="w-3 h-3" /> Editar
                  </button>
                  <button onClick={() => deleteDoctor(doc)} disabled={deleting === doc.id} className="text-xs text-rose-500 border border-rose-200 rounded-lg px-3 py-1.5 hover:bg-rose-50 disabled:opacity-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Doctor Modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Editar" : "Nuevo integrante del equipo"}
        size="lg"
        testid="modal-doctor"
        footer={
          <>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">Cancelar</button>
            <button onClick={() => wizardPaso ? onContinue() : save(newServices)} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? "Guardando…" : wizardPaso ? W.continuar : "Guardar"}
            </button>
            {wizardPaso && <button disabled={saving} onClick={onExit} className="px-4 py-2 border rounded-lg">{W.salir}</button>}
          </>
        }
      >
        {editing && <AvisoAgenda doctor={editing} sinSofi={sinSofi} testid="aviso-agenda-modal" />}
        {loadError && <p role="alert">{W.pendiente}</p>}
        <div className="space-y-5">
          {(!wizardPaso || wizardPaso === "medicos") && <>
          {/* Es médico */}
          <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50">
            <div>
              <div className="text-sm font-semibold text-slate-800">Es médico</div>
              <div className="text-xs text-slate-500">Apágalo para personal que da servicios sin ser médico (masajista, esteticista, enfermera…).</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.is_doctor}
              onClick={() => setForm({ ...form, is_doctor: !form.is_doctor })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${form.is_doctor ? "bg-brand-electric" : "bg-slate-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_doctor ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>

          {/* Nombre y especialidad/rol */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nombre completo">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dra. Mariana López" className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20" />
            </Field>
            {form.is_doctor ? (
              <Field label="Especialidad">
                <input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="Medicina estética" className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20" />
              </Field>
            ) : (
              <Field label="Rol">
                <input value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })} placeholder="Masajista" className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20" />
              </Field>
            )}
          </div>

          {/* Color */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Color</span>
            <div className="flex gap-2 mt-2">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                  className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
                  style={{ background: c, borderColor: form.color === c ? "#1e293b" : "transparent" }} />
              ))}
            </div>
          </div>

          </>}
          {/* Horario (doctor_availability, S5b) -- compartido, igual para médicos y prestadores */}
          {(!wizardPaso || wizardPaso === "horarios") && <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Horario</span>
            <div className="mt-2">
              <DoctorAvailabilityEditor value={availability} onChange={setAvailability} />
              {editing?.id && <AusenciasDoctor key={editing.id} doctorId={editing.id} sustantivo="profesional" />}
            </div>
          </div>}
          {/* Servicios propios */}
          {(!wizardPaso || wizardPaso === "servicios") && <div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Servicios que ofrece</span>
              {newServices.length > 0 && (
                <span className="text-[10px] font-bold bg-blue-500 text-white px-2 py-0.5 rounded-full">
                  {newServices.length} por guardar
                </span>
              )}
            </div>

            {/* Lista de servicios */}
            {allDisplayedServices.length > 0 ? (
              <div className="mt-2 space-y-1.5">
                {allDisplayedServices.map((svc) => (
                  <div key={svc._pending ? svc._key : svc.id}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${svc._pending ? "border-blue-200 bg-blue-50" : "border-slate-200 bg-slate-50"}`}>
                    <div className="flex-1 min-w-0">
                      <div>
                        <span className="text-sm font-medium text-slate-800">{svc.name}</span>
                        <span className="text-xs text-slate-500 ml-2">{svc.duration_minutes} min · {formatMXN(svc.price)}</span>
                        {svc._pending && <span className="ml-2 text-[10px] text-blue-500 font-semibold">Por guardar</span>}
                        {svc.requiere_anticipo && svc.anticipo_sugerido && (
                          <span className="ml-2 text-[11px] text-emerald-700" data-testid={`svc-anticipo-resumen-${svc.id ?? svc._key}`}>
                            {texto("servicio_resumen", { monto: formatoDinero(svc.anticipo_sugerido) })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5">
                        <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!svc.requires_approval}
                            onChange={() => svc._pending
                              ? setNewServices((prev) => prev.map((s) => s._key === svc._key ? { ...s, requires_approval: !s.requires_approval } : s))
                              : toggleExistingServiceFlag(svc, "requires_approval")}
                            className="w-3.5 h-3.5 rounded border-slate-300"
                          />
                          Requiere aprobación humana
                        </label>
                        {!sinSofi && (
                        <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={svc.bookable_by_assistant !== false}
                            onChange={() => svc._pending
                              ? setNewServices((prev) => prev.map((s) => s._key === svc._key ? { ...s, bookable_by_assistant: !(s.bookable_by_assistant !== false) } : s))
                              : toggleExistingServiceFlag(svc, "bookable_by_assistant")}
                            className="w-3.5 h-3.5 rounded border-slate-300"
                          />
                          Visible para el asistente
                        </label>
                        )}
                      </div>
                      {!svc._pending && puedeEditarAnticipo && (
                        <AnticipoServicio svc={svc} onGuardar={guardarAnticipoExistente} />
                      )}
                    </div>
                    <button type="button"
                      onClick={() => svc._pending ? removeNewService(svc._key) : removeExistingService(svc.id)}
                      className="text-rose-400 hover:text-rose-600 p-1 rounded shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-400 italic">Sin servicios agregados aún.</p>
            )}

            {/* Formulario inline agregar servicio */}
            <div className="mt-3 p-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="sm:col-span-1">
                  <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Nombre</label>
                  <input
                    ref={svcNameRef}
                    defaultValue=""
                    placeholder="Ej. Consulta general"
                    className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddService())}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Duración (min)</label>
                  <input
                    ref={svcDurationRef}
                    type="number" min="5" step="5"
                    defaultValue={30}
                    className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Precio (MXN)</label>
                  <input
                    ref={svcPriceRef}
                    type="number" min="0" step="50"
                    defaultValue=""
                    placeholder="0.00"
                    className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddService())}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <input ref={svcRequiresApprovalRef} type="checkbox" defaultChecked={false} className="w-3.5 h-3.5 rounded border-slate-300" />
                  Requiere aprobación humana
                </label>
                {!sinSofi && (
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <input ref={svcBookableRef} type="checkbox" defaultChecked={true} className="w-3.5 h-3.5 rounded border-slate-300" />
                  Visible para el asistente
                </label>
                )}
              </div>
              {puedeEditarAnticipo && (
              <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={nuevoRequiereAnticipo}
                         onChange={(e) => setNuevoRequiereAnticipo(e.target.checked)}
                         className="w-3.5 h-3.5 rounded border-slate-300" data-testid="svc-nuevo-requiere-anticipo" />
                  {TEXTOS.servicio_requiere}
                </label>
                {nuevoRequiereAnticipo && (
                  <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                    {TEXTOS.servicio_monto}
                    <input value={nuevoAnticipoMonto} onChange={(e) => setNuevoAnticipoMonto(e.target.value)}
                           inputMode="decimal" placeholder="0.00"
                           className="w-28 border border-slate-300 bg-white rounded-lg px-2 py-1 text-xs outline-none focus:border-blue-600"
                           data-testid="svc-nuevo-anticipo-monto" />
                  </label>
                )}
              </div>
              )}
              <button
                type="button"
                onClick={handleAddService}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-electric hover:text-blue-700"
              >
                <PlusCircle className="w-4 h-4" /> Agregar servicio
              </button>
            </div>
          </div>}
        </div>
      </Modal>
    </div>
  );
});
export default Doctors;

// CL.4 A2 -- casilla «Requiere anticipo» + monto sugerido de un servicio YA guardado. Se
// guarda con su propio botón (no con el «Guardar» general), como las banderas de arriba,
// porque el «Guardar» general no reescribe servicios existentes.
export function AnticipoServicio({ svc, onGuardar }) {
  const [requiere, setRequiere] = useState(!!svc.requiere_anticipo);
  const [monto, setMonto] = useState(svc.anticipo_sugerido || "");
  const [guardando, setGuardando] = useState(false);
  const cambiado = requiere !== !!svc.requiere_anticipo
    || (requiere && monto !== (svc.anticipo_sugerido || ""));
  const guardar = async () => {
    setGuardando(true);
    try { await onGuardar(svc, requiere, monto); } finally { setGuardando(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-1.5" data-testid={`svc-anticipo-${svc.id}`}>
      <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
        <input type="checkbox" checked={requiere} onChange={(e) => setRequiere(e.target.checked)}
               className="w-3.5 h-3.5 rounded border-slate-300" data-testid={`svc-requiere-anticipo-${svc.id}`} />
        {TEXTOS.servicio_requiere}
      </label>
      {requiere && (
        <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600">
        {TEXTOS.servicio_monto}
        <input value={monto} onChange={(e) => setMonto(e.target.value)} inputMode="decimal" placeholder="0.00"
               aria-label={TEXTOS.servicio_monto}
               className="w-24 border border-slate-300 bg-white rounded-lg px-2 py-0.5 text-[11px] outline-none focus:border-blue-600"
               data-testid={`svc-anticipo-monto-${svc.id}`} />
        </label>
      )}
      {cambiado && (
        <button type="button" onClick={guardar} disabled={guardando}
                className="text-[11px] font-medium text-brand-electric hover:underline disabled:opacity-60"
                data-testid={`svc-anticipo-guardar-${svc.id}`}>
          {TEXTOS.servicio_guardar}
        </button>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
